import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FIELD_TYPES, FREQUENCIES, fieldMeta, hasOptions } from "@/components/forms/fieldTypes";
import { FieldInput } from "@/components/forms/FieldInput";
import { BrandHeader } from "@/components/forms/BrandHeader";
import { CategoryPicker, useCategories } from "@/components/CategoriesDialog";
import { GripVertical, Trash2, ChevronUp, ChevronDown, Lock, Eye } from "lucide-react";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);
const BLANK = { title: "", description: "", kind: "checklist", frequency: "monthly", blocks_timesheet: false, shared: false, status: "draft", fields: [], assignment: { everyone: false, role_names: [], category_names: [], employee_ids: [] } };

function FieldEditor({ f, i, n, update, remove, move, dragProps }) {
  const M = fieldMeta(f.type);
  return (
    <div {...dragProps} data-testid={`builder-field-${i}`} className="bg-white border border-zinc-200 rounded-lg p-3 flex gap-2 transition-shadow hover:shadow-sm">
      <div className="flex flex-col items-center gap-0.5 text-zinc-400 pt-1">
        <GripVertical className="h-4 w-4 cursor-grab" />
        <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
        <button type="button" disabled={i === n - 1} onClick={() => move(i, 1)} className="disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
      </div>
      <div className="flex-1 space-y-2 min-w-0">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded shrink-0"><M.icon className="h-3 w-3" />{M.label}</span>
          <Input data-testid={`builder-field-label-${i}`} className="h-8" placeholder={f.type === "heading" ? "Section title" : "Question / item"} value={f.label} onChange={(e) => update(i, { label: e.target.value })} />
        </div>
        {f.type !== "heading" && <Input className="h-8 text-xs" placeholder="Help text (optional)" value={f.help || ""} onChange={(e) => update(i, { help: e.target.value })} />}
        {hasOptions(f.type) && (
          <Textarea data-testid={`builder-field-options-${i}`} rows={2} className="text-xs" placeholder="One option per line" value={(f.options || []).join("\n")} onChange={(e) => update(i, { options: e.target.value.split("\n") })} />
        )}
        {f.type !== "heading" && (
          <label className="flex items-center gap-2 text-xs text-zinc-600"><Switch data-testid={`builder-field-required-${i}`} checked={!!f.required} onCheckedChange={(v) => update(i, { required: v })} />Required</label>
        )}
      </div>
      <button type="button" data-testid={`builder-field-remove-${i}`} onClick={() => remove(i)} className="text-zinc-400 hover:text-rose-600 self-start"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

function QuestionsTab({ form, setForm }) {
  const [drag, setDrag] = useState(null);
  const fields = form.fields;
  const setFields = (fs) => setForm((p) => ({ ...p, fields: fs }));
  const add = (type) => setFields([...fields, { id: uid(), type, label: "", help: "", required: type !== "heading", options: hasOptions(type) ? ["Option 1"] : [] }]);
  const update = (i, patch) => setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const remove = (i) => setFields(fields.filter((_, j) => j !== i));
  const moveTo = (from, to) => { if (to < 0 || to >= fields.length || from === to) return; const fs = [...fields]; const [x] = fs.splice(from, 1); fs.splice(to, 0, x); setFields(fs); };
  return (
    <div className="grid md:grid-cols-[180px_1fr] gap-4">
      <div className="space-y-1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Add field</div>
        {FIELD_TYPES.map((t) => (
          <button key={t.v} type="button" data-testid={`add-field-${t.v}`} onClick={() => add(t.v)} className="w-full flex items-center gap-2 text-left text-xs px-2 py-1.5 rounded-md border border-zinc-200 bg-white hover:border-amber-400 hover:bg-amber-50 transition-colors"><t.icon className="h-3.5 w-3.5 text-slate-700" />{t.label}</button>
        ))}
      </div>
      <div className="space-y-2 min-h-[200px]">
        {fields.length === 0 && <div className="text-center text-sm text-zinc-400 border border-dashed border-zinc-300 rounded-lg py-14">Add fields from the left to build your form</div>}
        {fields.map((f, i) => (
          <FieldEditor key={f.id} f={f} i={i} n={fields.length} update={update} remove={remove} move={(j, d) => moveTo(j, j + d)}
            dragProps={{ draggable: true, onDragStart: () => setDrag(i), onDragOver: (e) => e.preventDefault(), onDrop: () => { moveTo(drag, i); setDrag(null); }, style: { opacity: drag === i ? 0.4 : 1 } }} />
        ))}
      </div>
    </div>
  );
}

function AssignTab({ form, setForm, roles, employees }) {
  const [cats] = useCategories();
  const a = form.assignment;
  const setA = (patch) => setForm((p) => ({ ...p, assignment: { ...p.assignment, ...patch } }));
  const toggle = (key, v) => setA({ [key]: a[key].includes(v) ? a[key].filter((x) => x !== v) : [...a[key], v] });
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">How often must it be completed?</Label>
          <Select value={form.frequency} onValueChange={(v) => setForm((p) => ({ ...p, frequency: v }))}>
            <SelectTrigger data-testid="builder-frequency"><SelectValue /></SelectTrigger>
            <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
          <Switch data-testid="builder-blocks-timesheet" checked={form.blocks_timesheet} onCheckedChange={(v) => setForm((p) => ({ ...p, blocks_timesheet: v }))} />
          <span className="text-xs"><span className="font-semibold text-slate-900 flex items-center gap-1"><Lock className="h-3.5 w-3.5" />Block timesheet submission</span>Assigned staff can't submit a timesheet until this is done for the current period.</span>
        </label>
        <label className="flex items-start gap-3 bg-sky-50 border border-sky-200 rounded-lg p-3 cursor-pointer sm:col-start-2">
          <Switch data-testid="builder-shared" checked={!!form.shared} onCheckedChange={(v) => setForm((p) => ({ ...p, shared: v }))} />
          <span className="text-xs"><span className="font-semibold text-slate-900 flex items-center gap-1"><Eye className="h-3.5 w-3.5" />View by all</span>Everyone this form is assigned to can view and download every submission in Library → Submitted Forms.</span>
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm font-medium"><Checkbox data-testid="assign-everyone" checked={a.everyone} onCheckedChange={(v) => setA({ everyone: !!v })} />Assign to all staff</label>
      {!a.everyone && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Staff categories</div>
          <CategoryPicker cats={cats} includeBuiltin value={a.category_names || []} onChange={(v) => setA({ category_names: v })} testid="assign-cat" />
        </div>
      )}
      {!a.everyone && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Job roles</div>
            <div className="border border-zinc-200 rounded-md p-2 max-h-56 overflow-y-auto space-y-1.5">
              {roles.length === 0 && <div className="text-xs text-zinc-400">No job roles set up yet</div>}
              {roles.map((r) => <label key={r.id} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`assign-role-${r.name}`} checked={a.role_names.includes(r.name)} onCheckedChange={() => toggle("role_names", r.name)} />{r.name}</label>)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Individual staff</div>
            <div className="border border-zinc-200 rounded-md p-2 max-h-56 overflow-y-auto space-y-1.5">
              {employees.map((e) => <label key={e.id} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`assign-emp-${e.id}`} checked={a.employee_ids.includes(e.id)} onCheckedChange={() => toggle("employee_ids", e.id)} />{e.first_name} {e.surname}<span className="text-[11px] text-zinc-400">{e.grade}</span></label>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewTab({ form, branding }) {
  const [vals, setVals] = useState({});
  return (
    <div className="max-w-xl mx-auto space-y-3 bg-[#FAFAFA] p-3 rounded-lg">
      <BrandHeader branding={branding} title={form.title || "Untitled form"} description={form.description} />
      {form.fields.map((f) => f.type === "heading"
        ? <div key={f.id} className="text-xs font-bold uppercase tracking-wider text-zinc-500 pt-2">{f.label}</div>
        : <div key={f.id} className="bg-white border border-zinc-200 rounded-lg p-3"><div className="text-sm font-medium mb-1.5">{f.label}{f.required && <span className="text-rose-500"> *</span>}</div>{f.help && <p className="text-xs text-zinc-400 -mt-1 mb-2">{f.help}</p>}<FieldInput field={{ ...f, options: (f.options || []).filter(Boolean) }} value={vals[f.id]} onChange={(v) => setVals((p) => ({ ...p, [f.id]: v }))} /></div>)}
    </div>
  );
}

export function FormBuilderDialog({ open, onOpenChange, editing, onSaved, readOnly }) {
  const [form, setForm] = useState(BLANK);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branding, setBranding] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? { ...BLANK, ...editing, assignment: { ...BLANK.assignment, ...(editing.assignment || {}) } } : BLANK);
    api.get("/job-roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get("/employees").then((r) => setEmployees((r.data || []).filter((e) => e.active !== false))).catch(() => {});
    api.get("/forms-settings").then((r) => setBranding(r.data)).catch(() => {});
  }, [open, editing]);

  const save = async (status) => {
    setSaving(true);
    const body = { ...form, status, fields: form.fields.map((f) => ({ ...f, options: (f.options || []).filter((o) => o.trim()) })) };
    try {
      if (editing) await api.put(`/forms/${editing.id}`, body); else await api.post("/forms", body);
      toast.success(status === "published" ? "Form published" : "Draft saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="form-builder-dialog">
        <DialogHeader><DialogTitle>{readOnly ? form.title || "Form" : editing ? "Edit form" : "New form / checklist"}</DialogTitle><DialogDescription>{readOnly ? "Read-only view. This form is managed by Amden." : "Build the questions, choose who completes it and how often."}</DialogDescription></DialogHeader>
        {readOnly ? <PreviewTab form={form} branding={branding} /> : <>
        <div className="grid sm:grid-cols-[1fr_160px] gap-3">
          <Input data-testid="builder-title" placeholder="e.g. Monthly Van Checklist" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger data-testid="builder-kind"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="checklist">Checklist</SelectItem><SelectItem value="form">Form</SelectItem></SelectContent>
          </Select>
          <Textarea data-testid="builder-description" rows={2} className="sm:col-span-2" placeholder="Instructions for staff (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <Tabs defaultValue="questions" className="mt-2">
          <TabsList>
            <TabsTrigger value="questions" data-testid="builder-tab-questions">Questions ({form.fields.length})</TabsTrigger>
            <TabsTrigger value="assign" data-testid="builder-tab-assign">Assign & schedule</TabsTrigger>
            <TabsTrigger value="preview" data-testid="builder-tab-preview">Preview</TabsTrigger>
          </TabsList>
          <TabsContent value="questions"><QuestionsTab form={form} setForm={setForm} /></TabsContent>
          <TabsContent value="assign"><AssignTab form={form} setForm={setForm} roles={roles} employees={employees} /></TabsContent>
          <TabsContent value="preview"><PreviewTab form={form} branding={branding} /></TabsContent>
        </Tabs>
        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100">
          <Button data-testid="builder-save-draft" variant="outline" disabled={saving} onClick={() => save("draft")}>Save as draft</Button>
          <Button data-testid="builder-publish" disabled={saving} onClick={() => save("published")} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">Save & publish</Button>
        </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}
